import type { UserInfo } from '@vben/types';

import type { LoginParams } from '#/modules/mes/auth';

import { ref } from 'vue';
import { useRouter } from 'vue-router';

import { LOGIN_PATH } from '@vben/constants';
import { preferences } from '@vben/preferences';
import { resetAllStores, useAccessStore, useUserStore } from '@vben/stores';

import { notification } from 'ant-design-vue';
import { defineStore } from 'pinia';

import { getUserInfoApi } from '#/api/core/user';
import {
  clearTokenCreatedAt,
  updateTokenCreatedAt,
} from '#/composables/useSessionTimeout';
import { $t } from '#/locales';
import { resetSessionInvalidated } from '#/api/request';
import { authApi } from '#/modules/mes/auth';
import { clearPendingBehaviors } from '#/utils/behavior-tracker';
import { clearPendingErrors } from '#/utils/error-handler';
import { clearPendingLogs } from '#/utils/performance-monitor';
import {
  clearPersistedRefreshToken,
  savePersistedRefreshToken,
} from '#/utils/token-sync';

export const useAuthStore = defineStore('auth', () => {
  const accessStore = useAccessStore();
  const userStore = useUserStore();
  const router = useRouter();

  const loginLoading = ref(false);

  /**
   * 异步处理登录操作
   * Asynchronously handle the login process
   * @param params 登录表单数据
   */
  async function authLogin(
    params: LoginParams,
    onSuccess?: () => Promise<void> | void,
  ) {
    if (loginLoading.value) {
      return {
        userInfo: null,
      };
    }

    // 异步处理用户登录操作并获取 accessToken
    let userInfo: null | UserInfo = null;
    try {
      loginLoading.value = true;

      // 调用登录API，后端返回 { user, token, token_type }
      // 注意：需要转换参数格式（camelCase → snake_case）
      const loginResult = await authApi.login({
        username: params.username,
        password: params.password,
        device_name: params.device_name,
        // device_id 会由请求拦截器自动添加到请求头 X-Device-ID
        // 后端从请求头读取，不需要在请求体中传递
        // device_id: params.deviceId,
      });
      const accessToken = loginResult.token;

      // 如果成功获取到 accessToken
      if (accessToken) {
        // 登录成功：复位会话失效标志，恢复 401 刷新通道
        resetSessionInvalidated();

        // 保存 token 到 accessStore
        accessStore.setAccessToken(accessToken);

        // 保存独立 Refresh Token（后端 2026-09 起签发；旧后端无此字段则清空，
        // 刷新通道自动回退旧的 access token 轮换端点）
        const refreshToken = loginResult.refreshToken ?? null;
        accessStore.setRefreshToken(refreshToken);
        savePersistedRefreshToken(refreshToken);

        // 方案B：更新 Token 创建时间，用于智能刷新判断
        updateTokenCreatedAt();

        // 注意：Token 保存后，需要依次获取用户信息和权限
        // 改为顺序调用，确保 Token 在请求拦截器中正确获取
        const fetchUserInfoResult = await fetchUserInfo();
        const accessCodes = await authApi.getPermissions();

        // 获取用户信息
        userInfo = fetchUserInfoResult;

        // 保存用户信息
        userStore.setUserInfo(userInfo);
        // 保存权限
        accessStore.setAccessCodes(accessCodes);

        // 如果登录过期，则设置登录过期为 false
        if (accessStore.loginExpired) {
          accessStore.setLoginExpired(false);
        } else {
          // 如果登录成功，则跳转到用户首页
          onSuccess
            ? await onSuccess?.()
            : await router.push(
                userInfo?.homePath || preferences.app.defaultHomePath,
              );
        }

        // 如果登录成功，则显示登录成功提示
        if (userInfo?.realName) {
          notification.success({
            description: `${$t('authentication.loginSuccessDesc')}:${userInfo?.realName}`,
            duration: 3,
            message: $t('authentication.loginSuccess'),
          });
        }
      }
    } finally {
      loginLoading.value = false;
    }

    return {
      userInfo,
    };
  }

  let isLoggingOut = false;

  async function logout(redirect: boolean = true) {
    if (isLoggingOut) return;
    isLoggingOut = true;
    try {
      // 1. 先调用后端 logout API（Token 还在，可以正确清理后端会话）
      const currentToken = accessStore.accessToken;
      const currentPath = router.currentRoute.value.fullPath;

      if (currentToken) {
        try {
          await authApi.logout();
        } catch (error) {
          // 忽略logout API错误（可能是 Token 已失效）
          // 原因：即使后端logout失败，前端也应该清除本地状态
          console.warn('[注销] 后端注销API失败（可能Token已失效）:', error);
        }
      }

      // 2. 清除本地 Token
      accessStore.setAccessToken(null);

      // 2.0 清除独立 Refresh Token（内存 + 明文持久层）
      accessStore.setRefreshToken(null);
      clearPersistedRefreshToken();

      // 2.1 清除 Token 创建时间（方案B）
      clearTokenCreatedAt();

      // 2.2 清除监控模块的待上报日志缓存
      // 避免退出后积累脏数据，登录后批量上传无效日志
      clearPendingErrors();
      clearPendingLogs();
      clearPendingBehaviors();

      // 3. 清除所有本地状态
      resetAllStores();
      accessStore.setLoginExpired(false);

      // 4. 跳转到登录页
      // 注意：必须在状态清除后手动跳转，因为 resetAllStores 可能已经改变了路由状态
      if (redirect) {
        // 使用 nextTick 确保状态清除完成后再跳转
        await new Promise((resolve) => setTimeout(resolve, 0));

        await router.replace({
          path: LOGIN_PATH,
          query: {
            redirect: encodeURIComponent(currentPath),
          },
        });
      }
    } finally {
      isLoggingOut = false;
    }
  }

  // 获取用户信息
  async function fetchUserInfo() {
    let userInfo: null | UserInfo = null;
    userInfo = await getUserInfoApi();
    userStore.setUserInfo(userInfo);
    return userInfo;
  }

  // 重置登录状态
  function $reset() {
    loginLoading.value = false;
  }

  return {
    $reset,
    authLogin,
    fetchUserInfo,
    loginLoading,
    logout,
  };
});
